<script setup>
  import { onMounted, ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useCartCommentStore } from '../../stores/CartCommentStore.js';
  import { formatDateTime } from '../../utils/format_offer.js';

  const props = defineProps({ cartId: { type: Number, required: true } });

  const { t } = useI18n();
  const commentStore = useCartCommentStore();
  const draft = ref('');
  const editing = ref(null);
  const BODY_LIMIT = 2000;

  // The comments are read once displayed: their "new" badge stays until the next visit
  onMounted(async () => {
    commentStore.comments = [];
    await commentStore.fetchComments(props.cartId);
    commentStore.markAsRead(props.cartId);
  });

  const initials = (name) => name.split(/\s+/).slice(0, 2).map(word => word[0]).join('').toUpperCase();

  const add = async () => {
    if (await commentStore.createComment(props.cartId, draft.value.trim())) draft.value = '';
  };

  const save = async () => {
    if (await commentStore.updateComment(props.cartId, editing.value.id, editing.value.body.trim())) editing.value = null;
  };

  const remove = (comment) => {
    if (confirm(t('cart.comments.confirm_delete'))) commentStore.deleteComment(props.cartId, comment.id);
  };
</script>

<template>
  <section id="cart-comments" class="cart-comments border rounded-3 bg-light p-3 mt-3 mb-4" aria-labelledby="cart-comments-title">
    <div class="d-flex align-items-center gap-2 flex-wrap">
      <h2 id="cart-comments-title" class="fs-5 mb-0">{{ t('cart.comments.title') }} ({{ commentStore.count }})</h2>
      <span class="badge rounded-pill text-bg-primary" v-if="commentStore.unreadCount">{{ t('cart.comments.new_count', commentStore.unreadCount, { count: commentStore.unreadCount }) }}</span>
    </div>
    <p class="text-muted small mb-2"><i class="bi bi-lock" aria-hidden="true"></i> {{ t('cart.comments.visibility') }}</p>

    <form class="mb-3" @submit.prevent="add">
      <label for="cart-comment-body" class="visually-hidden">{{ t('cart.comments.placeholder') }}</label>
      <textarea id="cart-comment-body" class="form-control" rows="2" :maxlength="BODY_LIMIT" v-model="draft" :placeholder="t('cart.comments.placeholder')"></textarea>
      <div class="d-flex align-items-center mt-2">
        <span class="text-danger small" role="alert" v-if="commentStore.error">{{ commentStore.error }}</span>
        <button type="submit" class="btn btn-primary btn-sm ms-auto" :disabled="!draft.trim()">{{ t('cart.comments.add') }}</button>
      </div>
    </form>

    <p class="text-muted small mb-0" v-if="!commentStore.comments.length">{{ t('cart.comments.empty') }}</p>
    <ul class="cart-comments__list list-unstyled mb-0" v-else>
      <li v-for="comment in commentStore.comments" :key="comment.id" class="bg-white border rounded-3 p-2 mb-2" :class="{ 'border-primary': comment.unread }">
        <p class="fst-italic text-muted small mb-0" v-if="comment.deleted_by">{{ t('cart.comments.deleted_by', { name: comment.deleted_by }) }}</p>
        <template v-else>
          <div class="d-flex align-items-center gap-2 flex-wrap small">
            <span class="cart-comments__avatar rounded-circle text-bg-secondary" aria-hidden="true">{{ initials(comment.author) }}</span>
            <span class="fw-semibold">{{ comment.author }}<template v-if="comment.mine"> {{ t('cart.comments.you') }}</template></span>
            <span class="badge text-bg-warning" v-if="comment.support">{{ t('cart.comments.support') }}</span>
            <span class="badge text-bg-primary" v-if="comment.unread">{{ t('cart.comments.new') }}</span>
            <span class="text-muted ms-auto">{{ formatDateTime(comment.created_at) }}<template v-if="comment.edited"> · {{ t('cart.comments.edited') }}</template></span>
          </div>
          <form class="mt-2" v-if="editing?.id === comment.id" @submit.prevent="save">
            <label :for="`cart-comment-${comment.id}`" class="visually-hidden">{{ t('cart.comments.edit') }}</label>
            <textarea :id="`cart-comment-${comment.id}`" class="form-control" rows="2" :maxlength="BODY_LIMIT" v-model="editing.body"></textarea>
            <div class="d-flex gap-2 justify-content-end mt-2">
              <button type="button" class="btn btn-outline-secondary btn-sm" @click="editing = null">{{ t('cart.comments.cancel') }}</button>
              <button type="submit" class="btn btn-primary btn-sm" :disabled="!editing.body.trim()">{{ t('cart.comments.save') }}</button>
            </div>
          </form>
          <template v-else>
            <p class="cart-comments__body mt-1 mb-0">{{ comment.body }}</p>
            <div class="d-flex gap-2 justify-content-end" v-if="comment.editable">
              <button type="button" class="btn btn-link btn-sm p-0" @click="editing = { id: comment.id, body: comment.body }">{{ t('cart.comments.edit') }}</button>
              <button type="button" class="btn btn-link btn-sm p-0 text-danger" @click="remove(comment)">{{ t('cart.comments.delete') }}</button>
            </div>
          </template>
        </template>
      </li>
    </ul>
  </section>
</template>

<style scoped>
  .cart-comments__list {
    max-height: 24rem;
    overflow-y: auto;
  }
  .cart-comments__avatar {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.75rem;
    height: 1.75rem;
    font-size: 0.75rem;
  }
  .cart-comments__body {
    white-space: pre-line;
    overflow-wrap: anywhere;
  }
</style>
